import { useMemo } from 'react';
import { CartItem, Product, ProductCartItem } from '../../types/common';
import { SECOND_PILLOW_DISCOUNT_PERCENT, THREE_PLUS_DISCOUNT_PERCENT } from '../../lib/cartPage/config';
import { getThreePlusEligibleIds } from '../../lib/cartPage/threePlus';
import { getSecondPillowDiscountQtyById } from '../../lib/cartPage/secondPillow';

export type ProductCartItemWithSecondPromo = ProductCartItem & {
    secondPillowAppliedQty?: number;
    secondPillowDiscountPercent?: number;
    secondPillowDiscountAmount?: number;
};

type UseCartPricingArgs = {
    cart: CartItem[];
    products: Product[];
    wishlist: string[];
    authUser: any;
};

export function useCartPricing({ cart, products, wishlist, authUser }: UseCartPricingArgs) {
    const threePlusEligibleIds = useMemo(() => getThreePlusEligibleIds(cart, products, authUser), [cart, products, authUser]);
    const secondPillowDiscountQtyById = useMemo(
        () => getSecondPillowDiscountQtyById(cart, products, authUser, threePlusEligibleIds),
        [cart, products, authUser, threePlusEligibleIds],
    );
    const hasSecondPillowPromo = useMemo(() => {
        let ok = false;
        secondPillowDiscountQtyById.forEach((v) => {
            if (v > 0) ok = true;
        });
        return ok;
    }, [secondPillowDiscountQtyById]);
    const cartProducts: ProductCartItemWithSecondPromo[] = useMemo(() => {
        if (!Array.isArray(cart) || !Array.isArray(products)) return [];
        return cart
            .filter((c) => products.find((pr) => pr.id === c.productId) !== undefined)
            .map((c) => {
                const p = products.find((pr) => pr.id === c.productId) as Product;
                const isEmployeeLocal = authUser?.status === 'employee';
                const baseEmployeePrice =
                    isEmployeeLocal && (p as any)?.specialDiscount?.discountedPrice != null ? (p as any).specialDiscount.discountedPrice : null;
                const baseDiscountPrice = (p as any)?.discount?.discountedPrice ?? null;
                const hasEmployeeSpecial = baseEmployeePrice != null;
                const hasRegularDiscount = baseDiscountPrice != null;
                let effectiveUnitPrice = (hasEmployeeSpecial ? baseEmployeePrice : null) ?? (hasRegularDiscount ? baseDiscountPrice : null) ?? p.price;
                const applyThreePlus = threePlusEligibleIds.has(c.productId) && !hasEmployeeSpecial && !hasRegularDiscount;
                if (applyThreePlus) {
                    effectiveUnitPrice = +(effectiveUnitPrice * (1 - THREE_PLUS_DISCOUNT_PERCENT / 100)).toFixed(2);
                }
                // акция "каждая вторая подушка -50%" (чётная, из самых дешёвых)
                const secondQtyRaw = secondPillowDiscountQtyById.get(c.productId) || 0;
                const qtyInt = Number.isFinite(Number(c.quantity)) && Number(c.quantity) > 0 ? Math.floor(Number(c.quantity)) : 1;
                const secondQty = !applyThreePlus && !hasEmployeeSpecial && !hasRegularDiscount ? Math.min(Math.max(0, Math.trunc(secondQtyRaw)), qtyInt) : 0;
                const secondDiscountAmount = secondQty > 0 ? +(effectiveUnitPrice * (SECOND_PILLOW_DISCOUNT_PERCENT / 100) * secondQty).toFixed(2) : 0;
                const effectiveTotalPrice = +(effectiveUnitPrice * c.quantity - secondDiscountAmount).toFixed(2);
                // "выгода" должна учитывать частичную скидку на подушки
                const baseProfit = (p.price - effectiveUnitPrice) * c.quantity;
                const unitProfit = +(baseProfit + secondDiscountAmount).toFixed(2);
                const unitWeight =
                    (p as any).grossWeight && (p as any).grossWeight > 0
                        ? (p as any).grossWeight
                        : (p as any).weight && (p as any).weight > 0
                          ? (p as any).weight
                          : (() => {
                                throw new Error(`У товара ${p.id} нет веса`);
                            })();
                const totalWeight = +(unitWeight * c.quantity).toFixed(3);
                return {
                    ...(c as any),
                    price: p.price,
                    totalPrice: effectiveTotalPrice,
                    profit: unitProfit,
                    name: p.name,
                    image: (p as any).image,
                    categoryUrl: (p as any).category?.url,
                    isWished: Array.isArray(wishlist) ? wishlist.includes(c.productId) : false,
                    availableQuantity: (p as any).quantity,
                    discount: (p as any)?.discount?.percent,
                    discountedPrice: applyThreePlus ? effectiveUnitPrice : (p as any)?.discount?.discountedPrice,
                    specialDiscountPercent: (p as any)?.specialDiscount?.percent,
                    specialDiscountedPrice: (p as any)?.specialDiscount?.discountedPrice,
                    weight: unitWeight,
                    totalWeight,
                    dimensions: (p as any).dimensions
                        ? { length: (p as any).dimensions.length, width: (p as any).dimensions.width, height: (p as any).dimensions.height }
                        : undefined,
                    articleNumber: (p as any).articleNumber,
                    group: (p as any).group,
                    nameDescription: (p as any).nameDescription,
                    fillerType: (p as any).fillerType,
                    fabricType: (p as any).fabricType,
                    secondPillowAppliedQty: secondQty > 0 ? secondQty : undefined,
                    secondPillowDiscountPercent: secondQty > 0 ? SECOND_PILLOW_DISCOUNT_PERCENT : undefined,
                    secondPillowDiscountAmount: secondQty > 0 ? secondDiscountAmount : undefined,
                };
            });
    }, [cart, products, wishlist, authUser, threePlusEligibleIds, secondPillowDiscountQtyById]);
    const totals = useMemo(() => {
        const price = +cartProducts.reduce((r, v) => r + (typeof v.totalPrice === 'number' ? v.totalPrice : 0), 0).toFixed(2);
        const weight = +cartProducts.reduce((r, v) => r + (typeof v.totalWeight === 'number' ? v.totalWeight : 0), 0).toFixed(3);
        const profit = cartProducts.reduce((r, v) => r + ((v as any).profit || 0), 0);
        const quantity = cartProducts.reduce((r, v) => r + (Number.isFinite(Number(v.quantity)) ? Number(v.quantity) : 0), 0);
        return { price, weight, profit, quantity };
    }, [cartProducts]);
    const flags = useMemo(() => {
        const cartHasDiscount = cartProducts.some((p) => (p as any).discountedPrice !== undefined);
        const cartHasSpecial = cartProducts.some((p) => (p as any).specialDiscountedPrice !== undefined);
        return { cartHasDiscount, cartHasSpecial };
    }, [cartProducts]);
    return {
        threePlusEligibleIds,
        secondPillowDiscountQtyById,
        hasSecondPillowPromo,
        cartProducts,
        totals,
        flags,
    };
}
